import React, { useCallback, useMemo, useState } from 'react';

import _, { get } from 'lodash';
import { MdAddCircle, MdInfo, MdRemoveCircle } from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { notApprovableRankIds } from '@modules/advancement/constants';
import {
  useUnapproveYouthAwardMutation,
  useUpdateItemPurchaseOrderMutation,
} from '@modules/rtk/esb-api';
import {
  Button,
  Fab,
  OfflineTooltip,
  PaginationTotal,
  SearchInput,
  T,
  Table,
  TableMobile,
  Tooltip,
} from '@modules/shared/components';
import {
  UnapproveTypes,
  advancementStatuses,
  advancementTypes,
  advancementTypesName,
  payloadStatusValues,
  unapproveAdvancementTypes,
} from '@modules/shared/constants';
import {
  offlineSel,
  organizationGuidSel,
  unitTypeIdSel,
} from '@modules/shared/duck/selectors';
import { intl } from '@modules/shared/localization';
import { toastService } from '@modules/toasts';
import { isMobileSel } from '@screen';
import { toggleArrayItem } from '@utils';

import pendingItemsListRows from '../../pendingItems/components/PendingItems/PendingItemsList/pendingItemsListRows';
import columns from '../../pendingItems/components/PendingItems/PendingItemsTable/pendingItemsTableColumns';
import {
  selectedRowKeysSel,
  sorterSel,
} from '../../pendingItems/duck/selectors';
import { POStatusTypes } from '../constants';
import {
  createPurchaseOrderLoadingSel,
  showClosedPOModal,
  updatePurchaseOrderLoadingSel,
} from '../duck';
import {
  PurchaseOrderTableItem,
  pendingItemsToPurchaseSel,
} from '../duck/selectorsTyped';
import {
  useGetPurchaseOrders,
  usePendingItemsToPurchaseTabQuery,
  usePurchaseOrderDetails,
} from '../hooks';
import {
  createPayloadAddItemPO,
  filterRanksPackOrTroop,
  searchOnItems,
} from '../utils';
import { ClosedPOModal } from './ClosedPOModal';
import { POCard } from './POCard';
import { POCardPlaceholder } from './POCardPlaceholder';
import styles from './ToPurchaseTab.less';

type SelectedItemKey = string | number;

interface ToPurchaseTabProps {
  handleGoToYouthProfile: (userId: number, activityType: string) => void;
  handleGoToAdultProfile: (userId: number, activityType: string) => void;
  handleSetSorter: (columnKey: string, order?: string) => void;
  handleSetSelectedRowKeys: (items: SelectedItemKey[]) => void;

  rowSelection?: {
    selectedRowKeys: string[];
    onChange: (rowKeys: string) => void;
    onSelectAll: (isSelectAll: boolean) => void;
  };
}

const ToPurchaseTab: React.FC<ToPurchaseTabProps> = props => {
  const {
    handleGoToYouthProfile: onGoToYouthProfile,
    handleGoToAdultProfile: onGoToAdultProfile,
    handleSetSorter: onSorterChange,
    handleSetSelectedRowKeys: onSetSelectedRowKeys,
  } = props;

  const dispatch = useDispatch();
  const isOffline = useSelector(offlineSel);
  const isMobile = useSelector(isMobileSel);
  const selectedOrgGuid = useSelector(organizationGuidSel);
  const updateLoading = useSelector(updatePurchaseOrderLoadingSel);
  const sorter = useSelector(sorterSel);
  const selectedRowKeys = useSelector(selectedRowKeysSel);
  const areAnyAdvancementsSelected = selectedRowKeys.length > 0;
  const [addItems, { isLoading: addItemsisLoading }] =
    useUpdateItemPurchaseOrderMutation();
  const {
    isLoading: isLoadingPendingItems,
    isFetching: isFetchingPendingItems,
  } = usePendingItemsToPurchaseTabQuery();
  const dataPendingItems = useSelector(pendingItemsToPurchaseSel);
  const unitTypeId = useSelector(unitTypeIdSel);
  const records = filterRanksPackOrTroop(dataPendingItems, unitTypeId);

  const [unapproveAward, { isLoading: unapprovingInProgress }] =
    useUnapproveYouthAwardMutation();

  const {
    data: listPO,
    isLoading: isLoadingListPO,
    isFetching: isFetchingListPO,
  } = useGetPurchaseOrders();

  const closedOrders =
    listPO?.filter(po => po.status === POStatusTypes.CLOSED) || [];
  const latestOpenOrder = _.chain(listPO)
    .filter(po => po.status === POStatusTypes.OPEN)
    .orderBy('dateCreated', 'desc')
    .head()
    .value();

  const { poid } = latestOpenOrder || {};

  const {
    data: detailsPO,
    isLoading: isLoadingDetailsPO,
    isFetching: isFetchingDetailsPO,
  } = usePurchaseOrderDetails({
    purchaseOrderId: poid,
  });

  const items = useMemo(
    () =>
      records?.filter(
        record =>
          !record.poid && record.status === advancementStatuses.APPROVED,
      ) || [],
    [records],
  );

  const [searchQuery, setSearchQuery] = useState('');

  const handleSearch = (search: string) => {
    setSearchQuery(search);
  };

  const searchedItems = searchOnItems(searchQuery, items);

  const selectedAdvancements = useMemo(
    () => items.filter(({ key }) => selectedRowKeys.includes(key)),
    [items, selectedRowKeys],
  );

  const formattedSelectedAdvancements = selectedAdvancements.map(adv => {
    const { advancementType, id, userAwardId, userId, versionId } = adv;

    return { advancementType, id, userAwardId, userId, versionId };
  });

  const handleShowClosedPOModal = () => {
    dispatch(showClosedPOModal());
  };

  const createPurchaseOrderLoading = useSelector(createPurchaseOrderLoadingSel);

  const showApproveButton = !_.isEmpty(detailsPO) && poid;
  const isLoading =
    isLoadingPendingItems ||
    isLoadingListPO ||
    isLoadingDetailsPO ||
    isFetchingPendingItems ||
    isFetchingListPO ||
    isFetchingDetailsPO ||
    createPurchaseOrderLoading ||
    updateLoading ||
    addItemsisLoading ||
    unapprovingInProgress;

  const handleTableChange = useCallback(
    (_pagination, _filters, sorter) => {
      onSorterChange(sorter);
    },
    [onSorterChange],
  );

  const handleSelectRow = useCallback(
    record => {
      const newSelectedKeys = toggleArrayItem(selectedRowKeys, record.key);
      onSetSelectedRowKeys(newSelectedKeys);
    },
    [onSetSelectedRowKeys, selectedRowKeys],
  );

  const renderPaginationTotal = useCallback(
    total => (
      <div className={styles.pendinTablePaginationContainer}>
        <Tooltip
          title={
            <FormattedMessage id="advancement.PendingItems.totalDisclaimer" />
          }
        >
          <MdInfo className={styles.infoIcon} />
        </Tooltip>
        <PaginationTotal total={total} selected={selectedRowKeys.length} />
      </div>
    ),
    [selectedRowKeys],
  );

  const pagination = {
    showTotal: renderPaginationTotal,
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSetSelectedRowKeys,
  };

  const handleUnapproveAdvancement = async () => {
    try {
      const hasNotApprovableRank = formattedSelectedAdvancements.some(
        adv =>
          adv.advancementType === advancementTypes.RANKS &&
          notApprovableRankIds.includes(adv.id),
      );

      if (hasNotApprovableRank) {
        toastService.warn(
          <FormattedMessage id="PurchaseOrder.PODetailsPage.unapprove.notApprovableRank" />,
        );
      } else {
        const payload = {
          organizationGuid: selectedOrgGuid,
          body: formattedSelectedAdvancements.map(adv => ({
            userId: adv.userId,
            organizationGuid: selectedOrgGuid,
            advancementType: unapproveAdvancementTypes[
              adv.advancementType as UnapproveTypes
            ] as advancementTypesName,
            advancementId: adv.id,
            userAwardId: null,
            newStatus: payloadStatusValues.completed,
          })),
        };

        await unapproveAward(payload).unwrap();

        toastService.success(
          <FormattedMessage id="PurchaseOrder.PODetailsPage.unapprove.success" />,
        );
        dispatch(onSetSelectedRowKeys([]));
      }
    } catch (error) {
      const errorMessage = get(error, ['data', 'errorDesc']);
      if (errorMessage) {
        toastService.error(errorMessage);
      }
    }
  };

  const handleAddItemPO = async () => {
    try {
      const payload = createPayloadAddItemPO(
        selectedOrgGuid,
        poid,
        formattedSelectedAdvancements,
      );

      await addItems(payload).unwrap();

      toastService.success(
        <FormattedMessage id="PurchaseOrder.PODetailsPage.success" />,
      );
    } catch (error) {
      const errorMessage = get(error, ['data', 'errorDesc']);
      if (errorMessage) {
        toastService.error(errorMessage);
      }
    }

    dispatch(onSetSelectedRowKeys([]));
  };

  const renderTitle = () => (
    <React.Fragment>
      {showApproveButton && (
        <OfflineTooltip placement="top">
          <Button
            id="create_purchase_order"
            disabled={isOffline || !areAnyAdvancementsSelected || !detailsPO}
            color={isMobile ? 'scouting-blue' : 'white'}
            ghost
            noBorder
            fitText
            icon={<MdAddCircle />}
            onClick={handleAddItemPO}
            uppercase={false}
          >
            <FormattedMessage id="PurchaseOrder.ToPurchaseTab.addItemsButton" />
          </Button>
        </OfflineTooltip>
      )}
      <OfflineTooltip placement="top">
        <Button
          disabled={isOffline || !areAnyAdvancementsSelected}
          color={isMobile ? 'scouting-blue' : 'white'}
          ghost
          noBorder
          fitText
          icon={<MdRemoveCircle />}
          onClick={handleUnapproveAdvancement}
          uppercase={false}
        >
          <FormattedMessage id="PurchaseOrder.ToPurchaseTab.unapproveItemsButton" />
        </Button>
      </OfflineTooltip>
    </React.Fragment>
  );

  return (
    <div className={styles.purchaseOrderPage}>
      <div className={styles.step}>
        <T className={styles.stepTitle} size="4">
          <FormattedMessage id="PurchaseOrder.ToPurchaseTab.step1" />
        </T>
        <p className={styles.stepDescription}>
          <FormattedMessage id="PurchaseOrder.ToPurchaseTab.step1Description" />
        </p>
        <div className={styles.itemTableContainer}>
          <SearchInput
            size="large"
            mobileSticky
            placeholder={intl.formatMessage({
              id: 'unitAdvancement.PendingItems.search',
            })}
            value={searchQuery}
            onChange={handleSearch}
          />
          {!isMobile && (
            <Table
              className={styles.table}
              size="small"
              fixedLayout
              dataSource={searchedItems}
              loading={isLoading}
              rowKey="key"
              title={renderTitle}
              columns={columns({
                // TODO: make a columns which is .ts
                sorter,
                onGoToYouthProfile,
                onGoToAdultProfile,
              })}
              pagination={pagination}
              sorter={sorter}
              rowSelection={rowSelection}
              onRow={(record: PurchaseOrderTableItem) => ({
                onClick: () => handleSelectRow(record),
              })}
              rowClassName={() => styles.rowItem}
              onChange={handleTableChange}
            />
          )}
          {isMobile && (
            <>
              <TableMobile
                items={searchedItems}
                loading={isLoading}
                rows={pendingItemsListRows({
                  onGoToYouthProfile,
                  onGoToAdultProfile,
                  isOffline,
                })}
                rowId="key"
                selectedKeys={selectedRowKeys}
                onItemClick={handleSelectRow}
              />
              <Fab content={renderTitle()} visible={areAnyAdvancementsSelected}>
                <FormattedMessage id="PurchaseOrder.ToPurchaseTab.chooseAnAction" />
              </Fab>
            </>
          )}
        </div>
      </div>
      <div className={styles.step}>
        <T className={styles.stepTitle} size="4">
          <FormattedMessage id="PurchaseOrder.ToPurchaseTab.step2" />
        </T>
        <p className={styles.stepDescription}>
          <FormattedMessage id="PurchaseOrder.ToPurchaseTab.step2Description" />
        </p>
        {!_.isEmpty(detailsPO) && poid ? (
          <POCard order={{ ...detailsPO, poid: poid }} isLoading={isLoading} />
        ) : (
          <POCardPlaceholder
            status={POStatusTypes.OPEN}
            selectedAdvancements={formattedSelectedAdvancements}
            createDisabled={!areAnyAdvancementsSelected}
            isLoading={isLoading}
            onSetSelectedRowKeys={onSetSelectedRowKeys}
          />
        )}
      </div>
      <div className={styles.step}>
        <T className={styles.stepTitle}>
          <FormattedMessage id="PurchaseOrder.ToPurchaseTab.step3" />
        </T>
        <p className={styles.stepDescription}>
          <FormattedMessage id="PurchaseOrder.ToPurchaseTab.step3Description" />
        </p>
        <div className={styles.step3InfoContainer}>
          <MdInfo className={styles.infoIcon} />
          <FormattedMessage id="PurchaseOrder.ToPurchaseTab.step3Info" />
        </div>
        <div className={styles.closedButtonContainer}>
          <Button
            onClick={handleShowClosedPOModal}
            uppercase={false}
            size="large"
            shape="round"
            ghost
            className={styles.closedPOButton}
            loading={isLoading}
          >
            <FormattedMessage
              id="PurchaseOrder.ToPurchaseTab.closedPOButton"
              values={{ count: closedOrders.length }}
            />
          </Button>
        </div>
        <ClosedPOModal />
      </div>
    </div>
  );
};

export default ToPurchaseTab;
